import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
  combineLatest, merge, of, Observable
} from 'rxjs';
import {
  takeUntil, mergeMap, concatMap, map, take, distinctUntilChanged, skip
} from 'rxjs/operators';

import { OFFER_TEMPLATE_CONTENT_TYPES } from 'app/constants';
import { Offer } from 'graphql/models/extended';
import { Branch, OfferTemplateSettings } from 'graphql/models/generated';
import { OfferPreview, OfferPreviewSettings } from 'models/offer-preview-settings';
import {
  OfferTemplateId, OfferTemplateSettingsContentType
} from 'models/offer-template-settings.model';
import { OfferBase } from 'models/offer.model';
import { Template, TemplateType } from 'models/template.model';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { OfferService } from 'shared/services/offer.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-offer-preview-settings',
  templateUrl: './offer-preview-settings.component.html',
  styleUrls: ['./offer-preview-settings.component.scss']
})
export class OfferPreviewSettingsComponent extends BaseComponent implements OnInit {
  form!: UntypedFormGroup;

  offer!: OfferBase;

  offerTitle?: string;

  rfp!: Offer | undefined;

  formReady = false;

  settings!: OfferTemplateSettings;

  offerPreviewSettings!: OfferPreviewSettings;

  branch!: Branch | undefined;

  templates!: Template[];

  templateId!: string;

  pdfSrc = '';

  disablePriceCalculation = false;

  radioData = {
    contentTypes: OFFER_TEMPLATE_CONTENT_TYPES
  };

  isDealer!: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private storeService: StoreService,
    private offerService: OfferService,
    private bsModalRef: BsModalRef,
    private coreDataService: CoreDataService,
    private cdref: ChangeDetectorRef,
    private translateService: TranslateService,
  ) { super(); }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.storeService.setFormReady(false);

    combineLatest([
      this.storeService.getCurrentOffer().pipe(take(1)),
      this.offerService.getRfpVersion().pipe(take(1)),
      this.storeService.isCurrentAccountDealer().pipe(take(1)),
      this.storeService.getOfferDisablePriceCalculation().pipe(take(1))
    ])
      .pipe(
        mergeMap(([offer, rfp, isDealer, disablePriceCalculation]: [OfferBase, Offer | undefined, boolean, boolean]) => {
          this.offer = offer;
          if(this.offer.documentId === rfp?.documentId) {
            this.rfp = rfp;
          }
          this.isDealer = isDealer;
          this.offerTitle = this.offerService.getCurrentOfferTitle(this.offer as Offer, this.isDealer);
          this.templateId = this.offer.textTemplateId || '';
          this.disablePriceCalculation = disablePriceCalculation;

          const isAnswerToRfp = !this.isDealer && this.rfp;
          if (isAnswerToRfp) {
            const buttonNameOnSecondStep = 'MODALS.OFFER_PREVIEW.SEND.SAVE_BUTTON_ANSWER_RFP';
            this.bsModalRef.content.currentModal.steps[1].footer.saveBtnName = buttonNameOnSecondStep;

            // TODO: Once manufacturer users have access to the dealers branch,
            // we can use the dealer settings for pdf generation. Then this line can be uncommented.
            // return of((this.rfp as Offer).branch as Branch);
          }

          return this.storeService.getCurrentAccountBranch();
        }),
        concatMap((branch: Branch | undefined) => {
          this.branch = branch;
          const branchId = this.branch ? this.branch.id : '';

          return branchId ? this.coreDataService.getTemplates(10, TemplateType.OFFER, branchId) : of([]);
        }),
        concatMap((templates: Template[]) => this.processTemplates(templates)),
        takeUntil(this.destroyed$)
      )
      .subscribe((templates: Template[]) => {
        if (!this.branch) return;

        this.setTemplates(templates);
        this.settings = this.branch.offerTemplateSettings as OfferTemplateSettings;
        this.setupForm();
      });
  }

  setTemplates(templates: Template[]): void {
    this.templates = templates;
    if (this.templateId === OfferTemplateId.CUSTOM) {
      const text = this.offer.textTemplatePreText as string;
      const text2 = this.offer.textTemplatePostText as string;

      this.addCustomTemplate(this.templateId, text, text2);
    }
  }

  processTemplates(templates: Template[]): Observable<Template[]> {
    const currentTemplateExists = templates.some((t) => t.id === this.templateId);

    // Handle cases where the current template doesn't exist.
    if (!currentTemplateExists) {
      return this.handleMissingCurrentTemplate(templates);
    }

    return of(templates);
  }

  private handleMissingCurrentTemplate(templates: Template[]): Observable<Template[]> {
    // Case when template is deleted or no templates are available.
    if (this.templateId || templates.length === 0) {
      this.templateId = OfferTemplateId.CUSTOM;

      return of(templates);
    }

    // Preselect the first template if no template selected.
    const firstTemplate = templates[0];
    if (firstTemplate && !this.templateId) {
      const { id = '', text = '', text2 = '' } = firstTemplate;

      if (id && text && text2) {
        this.templateId = id;

        return this.offerService.updateOfferTextTemplate(this.offer, id, text, text2).pipe(map(() => templates));
      }
    }

    return of(templates);
  }

  setupForm(): void {
    if (!this.settings.id || !this.offer.documentId) { return; }

    const settings: OfferTemplateSettings = this.settings;
    const currentTemplate = this.templates.find((t) => t.id === this.templateId);

    this.form = this.fb.group({
      content: [settings.content || OfferTemplateSettingsContentType.SHORT_DESCRIPTION],
      hidePictures: [!settings.displayPictures],
      hideConditions: [!settings.displayConditions],
      hidePrices: [!settings.displayPrices],
      showElementPriceOnly: [!settings.displayPrices ? false : !!settings.showElementPriceOnly],
      templates: [this.templateId],
      text: [currentTemplate ? currentTemplate.text : '', { updateOn: 'blur' }],
      text2: [currentTemplate ? currentTemplate.text2 : '', { updateOn: 'blur' }]
    }, { updateOn: 'change' }); // because of radios, we need change (not blur)

    this.form.disable({ emitEvent: false });
    this.formReady = true;
    this.storeService.setFormReady(this.formReady);
    this.createOfferPreview();
    this.detectFormChanges();
  }

  private addCustomTemplate(id: string, text: string, text2: string): void {
    this.templates.push({
      id,
      name: this.translateService.instant(('MODALS.OFFER_PREVIEW.SETTINGS.CUSTOMIZED')),
      text,
      text2
    });
  }

  private syncShowElementPriceOnlyEnabled(hidePrices: boolean): void {
    const ctrl = this.form.controls.showElementPriceOnly;
    if (hidePrices) {
      if (ctrl.value) {
        ctrl.setValue(false, { emitEvent: false });
      }
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  private detectFormChanges(): void {
    this.form.controls.hidePrices.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hidePrices: boolean) => {
        this.syncShowElementPriceOnlyEnabled(hidePrices);
      });

    merge(
      this.form.controls.content.valueChanges,
      this.form.controls.hidePictures.valueChanges,
      this.form.controls.hideConditions.valueChanges,
      this.form.controls.hidePrices.valueChanges,
      this.form.controls.showElementPriceOnly.valueChanges,
      this.form.controls.templates.valueChanges
        .pipe(
          map((id: string) => this.onChangeTemplate(id)),
          concatMap((val) => this.offerService.updateOfferTextTemplate(this.offer, val.id, val.text, val.text2))
        ),
      merge(
        this.form.controls.text.valueChanges.pipe(skip(1), distinctUntilChanged()),
        this.form.controls.text2.valueChanges.pipe(skip(1), distinctUntilChanged())
      ).pipe(
        map(() => this.onChangeTemplateTexts()),
        concatMap((val) => this.offerService.updateOfferTextTemplate(this.offer, val.id, val.text, val.text2))
      )
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.setPdfReady(false);
          this.createOfferPreview();
        },
        error: (err: any) => {
          console.info(err);
        }
      });
  }

  onChangeTemplate(id: string): Record<string, any> {
    const template = this.templates.find((t: Template) => t.id === id);
    const text = (template && template.text) || '';
    const text2 = (template && template.text2) || '';

    this.form.controls.text.setValue(text, { emitEvent: false });
    this.form.controls.text2.setValue(text2, { emitEvent: false });

    return { id, text, text2 };
  }

  onChangeTemplateTexts(): Record<string, any> {
    const id = OfferTemplateId.CUSTOM;
    const text = this.form.controls.text.value;
    const text2 = this.form.controls.text2.value;
    const customTemplateIndex = this.templates.findIndex((t) => t.id === id);

    if (customTemplateIndex === -1) {
      this.addCustomTemplate(id, text, text2);
    } else {
      this.templates[customTemplateIndex].text = text;
      this.templates[customTemplateIndex].text2 = text2;
    }

    this.form.controls.templates.setValue(id, { emitEvent: false });

    return { id, text, text2 };
  }

  createOfferPreview(): void {
    const {
      content, hidePictures, hideConditions, hidePrices, showElementPriceOnly
    } = this.form.getRawValue();

    const offerDocumentId = { documentId: this.offer.documentId, revision: this.offer.revision };
    this.offerPreviewSettings = {
      descriptionType: content,
      displayPictures: !hidePictures,
      displayConditions: !hideConditions,
      displayPrices: !hidePrices,
      showElementPriceOnly: !hidePrices && !!showElementPriceOnly,
      temporary: true
    };

    this.offerService.createOfferPreview(offerDocumentId, this.offerPreviewSettings)
      .subscribe((res: OfferPreview) => {
        this.pdfSrc = res.url;
      });
  }

  setPdfReady(isReady: boolean): void {
    if (!this.form) return;

    if (isReady) {
      this.form.enable({ emitEvent: false });
      this.syncShowElementPriceOnlyEnabled(this.form.controls.hidePrices.value);
      (this.form.statusChanges as any).emit('VALID');
    } else {
      this.form.disable({ emitEvent: false });
      (this.form.statusChanges as any).emit('INVALID');
    }
  }

  // needed to resolve console error for the form: ExpressionChangedAfterItHasBeenCheckedError
  ngAfterContentChecked(): void {
    this.cdref.detectChanges();
  }
}
